import { useEffect, useCallback, useRef } from "react";

export type ModifierKey = "meta" | "ctrl" | "alt" | "shift";

export interface ShortcutDefinition {
  key: string;
  modifiers?: ModifierKey[];
  action: () => void;
  description: string;
  category: "global" | "edit" | "outline" | "stream";
  enabled?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  shortcuts: ShortcutDefinition[];
  enabled?: boolean;
  ignoreInputElements?: boolean;
}

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}

function matchesModifiers(event: KeyboardEvent, modifiers: ModifierKey[] = []): boolean {
  const modifierState = {
    meta: event.metaKey,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    shift: event.shiftKey,
  };

  const requiredMeta = modifiers.includes("meta");
  const requiredCtrl = modifiers.includes("ctrl");
  const requiredAlt = modifiers.includes("alt");
  const requiredShift = modifiers.includes("shift");

  const platformPrimaryMod = isMac() ? modifierState.meta : modifierState.ctrl;
  const hasPrimaryModifier = requiredMeta || requiredCtrl;

  if (hasPrimaryModifier) {
    if (!platformPrimaryMod) return false;
    if (requiredAlt !== modifierState.alt) return false;
    if (requiredShift !== modifierState.shift) return false;
    return true;
  }

  return (
    modifierState.meta === requiredMeta &&
    modifierState.ctrl === requiredCtrl &&
    modifierState.alt === requiredAlt &&
    modifierState.shift === requiredShift
  );
}

function normalizeKey(key: string): string {
  const normalized = key.toLowerCase();
  if (normalized === "enter") return "Enter";
  if (normalized === "escape" || normalized === "esc") return "Escape";
  if (normalized === "tab") return "Tab";
  if (normalized === "space" || normalized === " ") return " ";
  if (normalized === "arrowup") return "ArrowUp";
  if (normalized === "arrowdown") return "ArrowDown";
  if (normalized === "arrowleft") return "ArrowLeft";
  if (normalized === "arrowright") return "ArrowRight";
  if (normalized === "?") return "?";
  return normalized;
}

function isInputElement(element: Element | null): boolean {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    (element as HTMLElement).isContentEditable
  );
}

export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
  ignoreInputElements = true,
}: UseKeyboardShortcutsOptions): void {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      if (ignoreInputElements && isInputElement(event.target as Element)) {
        if (event.key !== "Escape") return;
      }

      const eventKey = event.key === " " ? " " : event.key;

      for (const shortcut of shortcutsRef.current) {
        if (shortcut.enabled === false) continue;

        const normalizedShortcutKey = normalizeKey(shortcut.key);
        const normalizedEventKey = normalizeKey(eventKey);

        if (normalizedShortcutKey !== normalizedEventKey) continue;
        if (!matchesModifiers(event, shortcut.modifiers)) continue;

        event.preventDefault();
        event.stopPropagation();
        shortcut.action();
        return;
      }
    },
    [enabled, ignoreInputElements],
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, handleKeyDown]);
}

export function formatShortcut(shortcut: ShortcutDefinition): string {
  const mac = isMac();
  const parts: string[] = [];

  const modifiers = shortcut.modifiers || [];

  if (modifiers.includes("ctrl") || modifiers.includes("meta")) {
    parts.push(mac ? "\u2318" : "Ctrl");
  }
  if (modifiers.includes("shift")) {
    parts.push(mac ? "\u21E7" : "Shift");
  }
  if (modifiers.includes("alt")) {
    parts.push(mac ? "\u2325" : "Alt");
  }

  let keyDisplay = shortcut.key;
  if (keyDisplay === "Enter") keyDisplay = "\u21B5";
  else if (keyDisplay === "Escape") keyDisplay = "Esc";
  else if (keyDisplay === "Tab") keyDisplay = "\u21E5";
  else if (keyDisplay === " ") keyDisplay = "Space";
  else if (keyDisplay === "ArrowUp") keyDisplay = "\u2191";
  else if (keyDisplay === "ArrowDown") keyDisplay = "\u2193";
  else if (keyDisplay === "ArrowLeft") keyDisplay = "\u2190";
  else if (keyDisplay === "ArrowRight") keyDisplay = "\u2192";
  else keyDisplay = keyDisplay.toUpperCase();

  parts.push(keyDisplay);

  return mac ? parts.join("") : parts.join(" + ");
}

export function getModifierKeyLabel(): string {
  return isMac() ? "\u2318" : "Ctrl";
}
